import React from 'react';
import { Plus, Trash2, School } from 'lucide-react';
import { SchoolSegment, ProjectType } from '../../types/project.types';
import {
  SEGMENT_COLORS, segmentDotClass, nextSegmentColor, newSegmentId,
} from '../../utils/segmentColors';

interface SchoolSegmentsEditorProps {
  segments: SchoolSegment[];
  onChange: (segments: SchoolSegment[]) => void;
  projectType: ProjectType;
  // Default conversion-rate target applied to newly-added segments.
  defaultConversionRateTarget?: number;
}

/**
 * Manage the list of school segments (campuses) on a project. Each segment has
 * its own name + goals and a color used for pills throughout the app. Optional:
 * a project with no segments behaves as a single-school project.
 */
export const SchoolSegmentsEditor: React.FC<SchoolSegmentsEditorProps> = ({
  segments,
  onChange,
  projectType,
  defaultConversionRateTarget = 25,
}) => {
  const isEnrollment = projectType === 'enrollment';

  const addSegment = () => {
    const segment: SchoolSegment = {
      id: newSegmentId(),
      name: '',
      color: nextSegmentColor(segments.length),
      goals: { validLeads: 0, enrollmentTarget: 0, conversionRateTarget: defaultConversionRateTarget },
    };
    onChange([...segments, segment]);
  };

  const updateSegment = (id: string, patch: Partial<SchoolSegment>) => {
    onChange(segments.map(s => (s.id === id ? { ...s, ...patch } : s)));
  };

  const updateGoal = (id: string, field: 'validLeads' | 'enrollmentTarget', value: number) => {
    onChange(segments.map(s =>
      s.id === id ? { ...s, goals: { ...s.goals, [field]: value } } : s
    ));
  };

  const updateAddress = (id: string, field: 'address' | 'city' | 'state' | 'zipCode' | 'schoolPhone', value: string) => {
    onChange(segments.map(s => {
      if (s.id !== id) return s;
      const base = s.schoolAddress || { address: '', city: '', state: '', zipCode: '' };
      return { ...s, schoolAddress: { ...base, [field]: value } };
    }));
  };

  // HubSpot grade-banded campus codes (e.g. Metro North → MNE K-5, MNM 6-8).
  const addGradeBand = (seg: SchoolSegment) =>
    updateSegment(seg.id, { hubspotCampusGradeSplit: [...(seg.hubspotCampusGradeSplit || []), { code: '', minGrade: 0, maxGrade: 12 }] });
  const updateGradeBand = (seg: SchoolSegment, index: number, patch: Partial<{ code: string; minGrade: number; maxGrade: number }>) => {
    const bands = [...(seg.hubspotCampusGradeSplit || [])];
    bands[index] = { ...bands[index], ...patch };
    updateSegment(seg.id, { hubspotCampusGradeSplit: bands });
  };
  const removeGradeBand = (seg: SchoolSegment, index: number) =>
    updateSegment(seg.id, { hubspotCampusGradeSplit: (seg.hubspotCampusGradeSplit || []).filter((_, i) => i !== index) });

  const removeSegment = (id: string) => {
    onChange(segments.filter(s => s.id !== id));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <School className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-medium">Schools / Campuses</h3>
        <span className="text-sm text-gray-500">(Optional)</span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Add a segment per school to track separate goals and scheduling under one
        project. Leave empty for a single-school project. The school name is used
        as the "Campus" value in client CSV exports, so match it to their system.
      </p>

      <div className="space-y-3">
        {segments.map((segment, index) => (
          <div key={segment.id} className="border rounded-lg p-3 bg-gray-50">
            <div className="flex items-start gap-3">
              {/* Color picker */}
              <div className="pt-7">
                <button
                  type="button"
                  onClick={() => {
                    const i = SEGMENT_COLORS.indexOf((segment.color as any) || 'indigo');
                    updateSegment(segment.id, { color: nextSegmentColor(i + 1) });
                  }}
                  className={`w-5 h-5 rounded-full ${segmentDotClass(segment.color)} ring-2 ring-offset-1 ring-gray-200`}
                  title="Click to change color"
                />
              </div>

              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    School Name
                  </label>
                  <input
                    type="text"
                    value={segment.name}
                    onChange={(e) => updateSegment(segment.id, { name: e.target.value })}
                    placeholder={`School ${String.fromCharCode(65 + index)}`}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Valid Lead Goal
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={segment.goals.validLeads}
                    onChange={(e) => updateGoal(segment.id, 'validLeads', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {isEnrollment ? 'Enrollment Goal' : 'Enrollment Target'}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={segment.goals.enrollmentTarget}
                    onChange={(e) => updateGoal(segment.id, 'enrollmentTarget', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Per-campus address — used in this campus's parent confirmation emails */}
                <div className="sm:col-span-3 pt-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Campus address <span className="text-gray-400">(shown in this campus's confirmation emails)</span>
                  </label>
                  <input
                    type="text"
                    value={segment.schoolAddress?.address || ''}
                    onChange={(e) => updateAddress(segment.id, 'address', e.target.value)}
                    placeholder="Street address"
                    className="w-full px-3 py-2 border rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500 mb-2"
                  />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <input
                      type="text"
                      value={segment.schoolAddress?.city || ''}
                      onChange={(e) => updateAddress(segment.id, 'city', e.target.value)}
                      placeholder="City"
                      className="px-3 py-2 border rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <input
                      type="text"
                      value={segment.schoolAddress?.state || ''}
                      onChange={(e) => updateAddress(segment.id, 'state', e.target.value.toUpperCase().slice(0, 2))}
                      placeholder="ST"
                      maxLength={2}
                      className="px-3 py-2 border rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <input
                      type="text"
                      value={segment.schoolAddress?.zipCode || ''}
                      onChange={(e) => updateAddress(segment.id, 'zipCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                      placeholder="ZIP"
                      maxLength={5}
                      className="px-3 py-2 border rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <input
                      type="tel"
                      value={segment.schoolAddress?.schoolPhone || ''}
                      onChange={(e) => updateAddress(segment.id, 'schoolPhone', e.target.value)}
                      placeholder="Phone"
                      className="px-3 py-2 border rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* HubSpot campus code(s) — used by the client CSV export */}
                <div className="sm:col-span-3 pt-2 mt-1 border-t border-gray-200">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    HubSpot campus code <span className="text-gray-400">(client CSV export — defaults to the school name if blank)</span>
                  </label>
                  <input
                    type="text"
                    value={segment.hubspotCampus || ''}
                    onChange={(e) => updateSegment(segment.id, { hubspotCampus: e.target.value })}
                    placeholder="e.g. MV"
                    className="w-32 px-3 py-2 border rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-600">Grade-banded codes (optional)</span>
                      <button type="button" onClick={() => addGradeBand(segment)} className="text-xs text-indigo-600 hover:text-indigo-800">+ Add band</button>
                    </div>
                    {(segment.hubspotCampusGradeSplit || []).map((band, bi) => (
                      <div key={bi} className="flex items-center gap-2 mb-1">
                        <input
                          type="text"
                          value={band.code}
                          onChange={(e) => updateGradeBand(segment, bi, { code: e.target.value })}
                          placeholder="Code"
                          className="w-20 px-2 py-1 border rounded text-sm"
                        />
                        <span className="text-xs text-gray-500">grades</span>
                        <input
                          type="number"
                          value={band.minGrade}
                          onChange={(e) => updateGradeBand(segment, bi, { minGrade: parseInt(e.target.value) || 0 })}
                          className="w-16 px-2 py-1 border rounded text-sm"
                        />
                        <span className="text-xs text-gray-500">–</span>
                        <input
                          type="number"
                          value={band.maxGrade}
                          onChange={(e) => updateGradeBand(segment, bi, { maxGrade: parseInt(e.target.value) || 0 })}
                          className="w-16 px-2 py-1 border rounded text-sm"
                        />
                        <button type="button" onClick={() => removeGradeBand(segment, bi)} className="text-gray-400 hover:text-red-600" title="Remove band">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <p className="text-[11px] text-gray-400">Grades as numbers: K = 0, then 1–12. A student's grade picks the matching band; otherwise the fixed code (or school name) is used.</p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => removeSegment(segment.id)}
                className="pt-7 text-gray-400 hover:text-red-600"
                title="Remove school"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addSegment}
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-50"
      >
        <Plus className="w-4 h-4" />
        Add School
      </button>
    </div>
  );
};
